import {
  Shield,
  Cpu,
  Cloud,
  Users,
  CloudRain,
  Video,
  Globe,
  ArrowUpRight,
} from "lucide-react";

const ICONS = { Shield, Cpu, Cloud, Users, CloudRain, Video };

export default function About({ t }) {
  const platform = t.platform || t.about;
  const observatoryImg =
    "https://images.unsplash.com/photo-1778775782732-88d8dc0fcc10?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxuaWdodCUyMHNreSUyMG9ic2VydmF0b3J5JTIwZG9tZXxlbnwwfHx8fDE3ODE2MTY2MDN8MA&ixlib=rb-4.1.0&q=85";

  return (
    <section
      id="platform"
      data-testid="about-section"
      className="relative py-24 md:py-36 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Heading row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16">
          <div className="lg:col-span-7">
            <div className="font-mono text-[11px] tracking-[0.3em] text-[var(--primary)] mb-5">
              {platform.kicker}
            </div>
            <h2
              data-testid="about-title"
              className="font-display font-black uppercase leading-[1] text-3xl md:text-5xl"
            >
              <span className="block text-white">{platform.title}</span>
              <span className="block text-[var(--primary)]">
                {platform.titleAccent}
              </span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p
              data-testid="about-lead"
              className="text-white/70 text-base md:text-lg leading-relaxed"
            >
              {platform.lead}
            </p>
          </div>
        </div>

        {/* Image + paragraph */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
          <div
            data-testid="about-image"
            className="lg:col-span-7 relative border border-white/10 aspect-[16/10] overflow-hidden group"
          >
            <img
              src={observatoryImg}
              alt="Observatory dome under stars"
              className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-[1200ms]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#030508] via-[#030508]/30 to-transparent" />
            {/* Crosshair */}
            <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] text-white/70">
              CAM·01 // DOME
            </div>
            <div className="absolute top-4 right-4 font-mono text-[10px] tracking-[0.25em] text-[var(--primary)] flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--primary)] animate-pulse" />
              REC
            </div>
            <div className="absolute bottom-4 left-4 right-4 flex justify-between font-mono text-[10px] tracking-[0.25em] text-white/50">
              <span>AZ 218.4°</span>
              <span>ALT 67.2°</span>
              <span>SEEING 2.1″</span>
            </div>
            <span className="corner-tl !opacity-100 !w-4 !h-4" />
            <span className="corner-tr !opacity-100 !w-4 !h-4" />
            <span className="corner-bl !opacity-100 !w-4 !h-4" />
            <span className="corner-br !opacity-100 !w-4 !h-4" />
          </div>

          <div className="lg:col-span-5 flex flex-col justify-center">
            <div
              data-testid="about-body"
              className="space-y-5 text-white/65 text-base md:text-lg leading-relaxed"
            >
              <p>{platform.body}</p>
            </div>
            <div className="mt-8 font-mono text-[10px] tracking-[0.25em] uppercase">
              <div className="border-l border-[var(--primary)] pl-3 py-1">
                <div className="text-white/40 mb-1">DEPLOY</div>
                <div className="text-white">LOCAL · CLOUD</div>
              </div>
            </div>

            <a
              href="https://astro.scopelink.io/sob/public/observatories/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-public-observatories-cta"
              className="group mt-8 inline-flex items-center gap-3 border border-white/20 bg-white/[0.02] text-white font-mono text-[11px] tracking-[0.25em] uppercase font-semibold pl-5 pr-6 py-4 hover:border-[var(--primary)] hover:text-[var(--primary)] hover:bg-[var(--primary)]/5 transition-all"
            >
              <Globe className="h-4 w-4" strokeWidth={1.5} />
              <span>{platform.publicCta}</span>
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </div>

        {/* Feature grid */}
        <div
          data-testid="about-features"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10"
        >
          {platform.features.map((f, i) => {
            const Icon = ICONS[f.icon] || Shield;
            const code = String(i + 1).padStart(2, "0");
            return (
              <div
                key={i}
                data-testid={`feature-${i}`}
                className="group bg-[#030508] hover:bg-[#0b101e] p-8 transition-colors duration-300 relative"
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="inline-flex h-12 w-12 items-center justify-center border border-white/15 group-hover:border-[var(--primary)] transition-colors">
                    <Icon
                      className="h-5 w-5 text-white group-hover:text-[var(--primary)] transition-colors"
                      strokeWidth={1.5}
                    />
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.3em] text-white/30">
                    {code}
                  </span>
                </div>
                <h3 className="font-display text-lg md:text-xl font-semibold text-white mb-2">
                  {f.title}
                </h3>
                <p className="text-white/55 text-sm leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
